import logger from '../lib/logger';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Layout from '../components/Layout';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
  Plus, Search, MapPin, Building2, Eye, ChevronRight, X,
  Upload, ArrowLeft, User, Phone, Trash2, RefreshCw, Loader2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { toast } from 'sonner';

const FLAT_STATUSES = [
  { value: 'all', label: 'All Status' },
  { value: 'available', label: 'Available' },
  { value: 'booked', label: 'Booked' },
  { value: 'sold', label: 'Sold' },
  { value: 'negotiation', label: 'Negotiation' },
];

const BHK_TYPES = [
  { value: 'all', label: 'All BHK' },
  { value: '1 BHK', label: '1 BHK' },
  { value: '2 BHK', label: '2 BHK' },
  { value: '3 BHK', label: '3 BHK' },
  { value: '4 BHK', label: '4 BHK' },
  { value: '5 BHK', label: '5 BHK' },
  { value: 'Penthouse', label: 'Penthouse' },
  { value: 'Studio', label: 'Studio' },
];

const FURNISHING_OPTIONS = [
  { value: 'furnished', label: 'Furnished' },
  { value: 'semi-furnished', label: 'Semi-Furnished' },
  { value: 'unfurnished', label: 'Unfurnished' },
];

const STATUS_COLORS = {
  available: { bg: 'bg-green-100', text: 'text-green-700' },
  booked: { bg: 'bg-blue-100', text: 'text-blue-700' },
  sold: { bg: 'bg-red-100', text: 'text-red-700' },
  negotiation: { bg: 'bg-amber-100', text: 'text-amber-700' },
};

const Properties = () => {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [projectDetail, setProjectDetail] = useState(null);
  const [flats, setFlats] = useState([]);
  const [filteredFlats, setFilteredFlats] = useState([]);
  const [selectedFlat, setSelectedFlat] = useState(null);
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [isFlatModalOpen, setIsFlatModalOpen] = useState(false);
  const [isFlatDetailOpen, setIsFlatDetailOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterBhk, setFilterBhk] = useState('all');
  const [filterTower, setFilterTower] = useState('all');
  const [filterFloor, setFilterFloor] = useState('all');
  const [globalSearch, setGlobalSearch] = useState('');
  const [globalResults, setGlobalResults] = useState([]);
  const [showGlobalResults, setShowGlobalResults] = useState(false);
  const [agents, setAgents] = useState([]);
  const [leads, setLeads] = useState([]);
  const [autoPopLoading, setAutoPopLoading] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  // Inventory sync
  const [syncing, setSyncing] = useState(false);

  const handleInventorySync = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/inventory/sync');
      if (res.data.already_running) { toast.info('Sync already in progress'); }
      else { toast.success('Inventory sync started'); }
      const poll = setInterval(async () => {
        try {
          const st = await api.get('/inventory/sync/status');
          const latest = st.data.logs?.[0];
          if (latest && latest.status !== 'running') {
            clearInterval(poll);
            setSyncing(false);
            if (latest.status === 'success') {
              const f = latest.flats || {};
              toast.success(`Sync done: ${f.added || 0} added, ${f.updated || 0} updated`);
              fetchProjects();
            } else {
              toast.error(`Sync failed: ${latest.error || 'Unknown error'}`);
            }
          }
        } catch { /* ignore */ }
      }, 5000);
      setTimeout(() => { clearInterval(poll); setSyncing(false); }, 300000);
    } catch (e) {
      toast.error('Failed to start sync');
      setSyncing(false);
    }
  };

  const [projectForm, setProjectForm] = useState({ name: '', location: '', description: '' });
  const [flatForm, setFlatForm] = useState({
    flat_number: '', floor: '', tower_block: '', bhk_type: '2 BHK',
    size_sqft: '', price: '', facing: '', parking: '', furnishing: 'unfurnished',
    status: 'available', notes: '', linked_lead_id: '', assigned_agent: '',
  });

  useEffect(() => { fetchProjects(); fetchAgents(); fetchLeads(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedProject) {
      fetchProjectDetail(selectedProject.id);
      fetchFlats(selectedProject.id);
    }
  }, [selectedProject]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { filterFlatsData(); }, [flats, searchTerm, filterStatus, filterBhk, filterTower, filterFloor]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProjects = async () => {
    try {
      const res = await api.get('/flat-projects');
      setProjects(res.data);
    } catch (e) { toast.error('Failed to fetch projects'); }
  };

  const fetchProjectDetail = async (id) => {
    try {
      const res = await api.get(`/flat-projects/${id}`);
      setProjectDetail(res.data);
    } catch (e) { logger.error('Failed to fetch project detail'); }
  };

  const fetchFlats = async (projectId) => {
    try {
      const res = await api.get(`/flats?project_id=${projectId}`);
      setFlats(res.data);
    } catch (e) { toast.error('Failed to fetch flats'); }
  };

  const fetchAgents = async () => {
    try {
      const res = await api.get('/team/agents');
      setAgents(res.data);
    } catch (e) { logger.error('Failed to fetch agents'); }
  };

  const fetchLeads = async () => {
    try {
      const res = await api.get('/leads');
      setLeads(res.data);
    } catch (e) { logger.error('Failed to fetch leads'); }
  };

  const filterFlatsData = useCallback(() => {
    let f = flats;
    if (searchTerm) {
      const t = searchTerm.toLowerCase();
      f = f.filter(p => p.flat_number?.toLowerCase().includes(t) || p.tower_block?.toLowerCase().includes(t));
    }
    if (filterStatus !== 'all') f = f.filter(p => p.status === filterStatus);
    if (filterBhk !== 'all') f = f.filter(p => p.bhk_type === filterBhk);
    if (filterTower !== 'all') f = f.filter(p => p.tower_block === filterTower);
    if (filterFloor !== 'all') f = f.filter(p => p.floor === filterFloor);
    setFilteredFlats(f);
  }, [flats, searchTerm, filterStatus, filterBhk, filterTower, filterFloor]);

  const handleGlobalSearch = async (q) => {
    setGlobalSearch(q);
    if (q.length < 2) { setGlobalResults([]); setShowGlobalResults(false); return; }
    try {
      const res = await api.get(`/flats/search?q=${encodeURIComponent(q)}`);
      setGlobalResults(res.data);
      setShowGlobalResults(true);
    } catch (e) { logger.error('Search failed'); }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      await api.post('/flat-projects', projectForm);
      toast.success('Project created!');
      setIsProjectModalOpen(false);
      setProjectForm({ name: '', location: '', description: '' });
      fetchProjects();
    } catch (e) { toast.error('Failed to create project'); }
  };

  const handleCreateFlat = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...flatForm,
        project_id: selectedProject.id,
        size_sqft: flatForm.size_sqft ? parseFloat(flatForm.size_sqft) : null,
        price: flatForm.price ? parseFloat(flatForm.price) : null,
        linked_lead_id: flatForm.linked_lead_id || null,
        assigned_agent: flatForm.assigned_agent || null,
      };
      await api.post('/flats', data);
      toast.success('Flat added!');
      setIsFlatModalOpen(false);
      setFlatForm({ flat_number: '', floor: '', tower_block: '', bhk_type: '2 BHK', size_sqft: '', price: '', facing: '', parking: '', furnishing: 'unfurnished', status: 'available', notes: '', linked_lead_id: '', assigned_agent: '' });
      fetchFlats(selectedProject.id);
      fetchProjectDetail(selectedProject.id);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to add flat'); }
  };

  const handleStatusUpdate = async (flatId, status) => {
    try {
      await api.put(`/flats/${flatId}/status?status=${status}`);
      toast.success('Status updated!');
      fetchFlats(selectedProject.id);
      fetchProjectDetail(selectedProject.id);
      if (selectedFlat?.id === flatId) setSelectedFlat({ ...selectedFlat, status });
    } catch (e) { toast.error('Failed to update status'); }
  };

  const handleBhkUpdate = async (flatId, bhkType) => {
    try {
      await api.put(`/flats/${flatId}/bhk-type?bhk_type=${encodeURIComponent(bhkType)}`);
      toast.success('BHK type updated!');
      fetchFlats(selectedProject.id);
      if (selectedFlat?.id === flatId) setSelectedFlat({ ...selectedFlat, bhk_type: bhkType });
    } catch (e) { toast.error('Failed to update BHK type'); }
  };

  const handleDeleteFlat = async (flatId) => {
    if (!window.confirm('Are you sure you want to delete this flat?')) return;
    try {
      await api.delete(`/flats/${flatId}`);
      toast.success('Flat deleted!');
      fetchFlats(selectedProject.id);
      fetchProjectDetail(selectedProject.id);
      if (selectedFlat?.id === flatId) setIsFlatDetailOpen(false);
    } catch (e) { toast.error('Failed to delete flat'); }
  };

  const handleFlatPatch = async (flatId, field, value) => {
    try {
      await api.patch(`/flats/${flatId}`, { [field]: value });
      toast.success('Updated!');
      fetchFlats(selectedProject.id);
      if (selectedFlat?.id === flatId) setSelectedFlat(prev => ({ ...prev, [field]: value }));
    } catch (e) { toast.error('Failed to update'); }
  };

  const handleAssignLead = async (flatId, leadId) => {
    try {
      await api.put(`/flats/${flatId}/assign-lead?lead_id=${leadId}`);
      toast.success('Lead linked!');
      fetchFlats(selectedProject.id);
      const lead = leads.find(l => l.id === leadId);
      if (selectedFlat?.id === flatId) {
        const current = selectedFlat.linked_leads || [];
        setSelectedFlat({ ...selectedFlat, linked_leads: [...current, { id: leadId, name: lead?.name, phone: lead?.phone }] });
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to link lead'); }
  };

  const handleRemoveLeadFromFlat = async (flatId, leadId) => {
    try {
      await api.delete(`/flats/${flatId}/leads/${leadId}`);
      toast.success('Lead removed!');
      fetchFlats(selectedProject.id);
      if (selectedFlat?.id === flatId) {
        const current = selectedFlat.linked_leads || [];
        setSelectedFlat({ ...selectedFlat, linked_leads: current.filter(l => l.id !== leadId) });
      }
    } catch (e) { toast.error('Failed to remove lead'); }
  };

  const handleAssignAgent = async (flatId, agentId) => {
    try {
      await api.put(`/flats/${flatId}/assign-agent?agent_id=${agentId}`);
      toast.success('Agent assigned!');
      fetchFlats(selectedProject.id);
      const agent = agents.find(a => a.id === agentId);
      if (selectedFlat?.id === flatId) setSelectedFlat({ ...selectedFlat, assigned_agent: agentId, assigned_agent_name: agent?.name });
    } catch (e) { toast.error('Failed to assign agent'); }
  };

  const handleAutoPopulate = async () => {
    setAutoPopLoading(true);
    try {
      const res = await api.post('/integrations/99acres/auto-populate');
      const s = res.data.stats;
      toast.success(`Auto-populated: ${s.flats_created} flats, ${s.plots_created} plots, ${s.projects_created} projects created, ${s.skipped} skipped`);
      fetchProjects();
    } catch (e) { toast.error('Failed to auto-populate'); }
    setAutoPopLoading(false);
  };

  const formatPrice = (price) => {
    if (!price || price === 0) return '—';
    const p = typeof price === 'string' ? parseFloat(price.replace(/,/g, '')) : price;
    if (isNaN(p) || p === 0) return '—';
    if (p >= 10000000) return `₹${(p / 10000000).toFixed(2)} Cr`;
    if (p >= 100000) return `₹${(p / 100000).toFixed(1)} L`;
    return `₹${p.toLocaleString('en-IN')}`;
  };

  const getStatusBadge = (status) => {
    const c = STATUS_COLORS[status] || STATUS_COLORS.available;
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${c.bg} ${c.text}`}>{status}</span>;
  };

  const uniqueTowers = useMemo(() => [...new Set(flats.map(f => f.tower_block).filter(Boolean))], [flats]);
  const uniqueFloors = useMemo(() => [...new Set(flats.map(f => f.floor).filter(Boolean))].sort(), [flats]);

  // ================ PROJECT LIST VIEW ================
  if (!selectedProject) {
    return (
      <Layout>
        <div className="p-3 lg:p-8 bg-slate-50 min-h-screen">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-4 lg:mb-6 gap-3 lg:gap-4">
            <div>
              <h1 className="aavaas-page-title" data-testid="properties-title">Flats</h1>
              <p className="text-slate-600 mt-1">Manage flat projects, apartments, and availability</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
              <div className="relative flex-1 lg:flex-initial">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input placeholder="Search flats (e.g., 3 BHK in Aerocity)..." className="pl-10 w-full lg:w-[280px] bg-white" value={globalSearch} onChange={(e) => handleGlobalSearch(e.target.value)} data-testid="global-flat-search" />
                {showGlobalResults && globalResults.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border z-50 max-h-80 overflow-y-auto">
                    <div className="p-2 text-xs text-slate-500 border-b">{globalResults.length} flats found</div>
                    {globalResults.slice(0, 20).map(f => (
                      <div key={f.id} className="p-2 hover:bg-slate-50 cursor-pointer border-b border-slate-50 flex items-center justify-between" onClick={() => {
                        setShowGlobalResults(false); setGlobalSearch('');
                        const proj = projects.find(pr => pr.id === f.project_id);
                        if (proj) { setSelectedProject(proj); setTimeout(() => { setSelectedFlat(f); setIsFlatDetailOpen(true); }, 500); }
                      }}>
                        <div>
                          <span className="font-medium text-sm">{f.project_name} - {f.tower_block || ''} {f.flat_number}</span>
                          <span className="text-xs text-slate-500 ml-2">{f.bhk_type} | {f.size_sqft ? `${f.size_sqft} sq.ft` : ''}</span>
                        </div>
                        {getStatusBadge(f.status)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {isAdmin && (
                <>
                  <Button variant="outline" size="sm" onClick={handleInventorySync} disabled={syncing} data-testid="sync-flats-btn" className="gap-1.5">
                    {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    {syncing ? 'Syncing...' : 'Sync Inventory'}
                  </Button>
                  <Button variant="outline" onClick={handleAutoPopulate} disabled={autoPopLoading} data-testid="auto-populate-99acres">
                    <Upload className="w-4 h-4 mr-2" /> {autoPopLoading ? 'Populating...' : '99acres Auto-Populate'}
                  </Button>
                  <Dialog open={isProjectModalOpen} onOpenChange={setIsProjectModalOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-slate-900 hover:bg-slate-800" data-testid="add-flat-project-button"><Plus className="w-4 h-4 mr-2" /> Add Project</Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle>Add Apartment Project</DialogTitle></DialogHeader>
                      <form onSubmit={handleCreateProject} className="space-y-4 mt-4">
                        <div><Label>Project Name *</Label><Input value={projectForm.name} onChange={(e) => setProjectForm({ ...projectForm, name: e.target.value })} required data-testid="flat-project-name-input" /></div>
                        <div><Label>Location</Label><Input value={projectForm.location} onChange={(e) => setProjectForm({ ...projectForm, location: e.target.value })} placeholder="e.g., Mohali" /></div>
                        <div><Label>Description</Label><Textarea value={projectForm.description} onChange={(e) => setProjectForm({ ...projectForm, description: e.target.value })} rows={3} /></div>
                        <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" data-testid="submit-flat-project">Create Project</Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
            {projects.map(project => (
              <Card key={project.id} className="bg-white border-0 shadow-sm hover:shadow-md transition-shadow cursor-pointer" onClick={() => setSelectedProject(project)} data-testid={`flat-project-card-${project.id}`}>
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900">{project.name}</h3>
                      {project.location && <p className="text-sm text-slate-500 flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" />{project.location}</p>}
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-400" />
                  </div>
                  {project.description && <p className="text-sm text-slate-600 mb-4 line-clamp-2">{project.description}</p>}
                  <div className="grid grid-cols-4 gap-2">
                    <div className="text-center p-2 bg-slate-50 rounded">
                      <p className="text-lg font-bold text-slate-900">{project.total_flats}</p>
                      <p className="text-[10px] text-slate-500">Total</p>
                    </div>
                    <div className="text-center p-2 bg-green-50 rounded">
                      <p className="text-lg font-bold text-green-600">{project.available_flats}</p>
                      <p className="text-[10px] text-green-600">Available</p>
                    </div>
                    <div className="text-center p-2 bg-blue-50 rounded">
                      <p className="text-lg font-bold text-blue-600">{project.booked_flats}</p>
                      <p className="text-[10px] text-blue-600">Booked</p>
                    </div>
                    <div className="text-center p-2 bg-red-50 rounded">
                      <p className="text-lg font-bold text-red-600">{project.sold_flats}</p>
                      <p className="text-[10px] text-red-600">Sold</p>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
          {projects.length === 0 && (
            <div className="text-center py-16">
              <Building2 className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <p className="text-lg text-slate-500 font-medium">No apartment projects yet</p>
              <p className="text-sm text-slate-400">Add a project or use 99acres Auto-Populate to get started</p>
            </div>
          )}
        </div>
      </Layout>
    );
  }

  // ================ PROJECT DETAIL VIEW ================
  return (
    <Layout>
      <div className="aavaas-page">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-6 gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => { setSelectedProject(null); setFlats([]); setFilteredFlats([]); }} data-testid="back-to-flat-projects">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900" data-testid="flat-project-detail-title">{selectedProject.name}</h1>
              <p className="text-slate-500 text-sm">{selectedProject.location || 'Flat Inventory'}</p>
            </div>
          </div>
          <div className="flex gap-2">
            {isAdmin && (
              <Button className="bg-slate-900 hover:bg-slate-800" onClick={() => setIsFlatModalOpen(true)} data-testid="add-flat-button">
                <Plus className="w-4 h-4 mr-2" /> Add Flat
              </Button>
            )}
          </div>
        </div>

        {projectDetail && (
          <div className="grid grid-cols-3 md:grid-cols-5 gap-3 mb-6">
            <Card className="p-3 bg-white border-0 shadow-sm text-center">
              <p className="text-2xl font-bold text-slate-900">{projectDetail.total_flats}</p>
              <p className="text-[10px] text-slate-500">Total Flats</p>
            </Card>
            <Card className="p-3 bg-white border-0 shadow-sm text-center cursor-pointer hover:ring-2 ring-green-200" onClick={() => setFilterStatus('available')}>
              <p className="text-2xl font-bold text-green-600">{projectDetail.available_flats}</p>
              <p className="text-[10px] text-green-600">Available</p>
            </Card>
            <Card className="p-3 bg-white border-0 shadow-sm text-center cursor-pointer hover:ring-2 ring-blue-200" onClick={() => setFilterStatus('booked')}>
              <p className="text-2xl font-bold text-blue-600">{projectDetail.booked_flats}</p>
              <p className="text-[10px] text-blue-600">Booked</p>
            </Card>
            <Card className="p-3 bg-white border-0 shadow-sm text-center cursor-pointer hover:ring-2 ring-red-200" onClick={() => setFilterStatus('sold')}>
              <p className="text-2xl font-bold text-red-600">{projectDetail.sold_flats}</p>
              <p className="text-[10px] text-red-600">Sold</p>
            </Card>
            <Card className="p-3 bg-white border-0 shadow-sm text-center cursor-pointer hover:ring-2 ring-amber-200" onClick={() => setFilterStatus('negotiation')}>
              <p className="text-2xl font-bold text-amber-600">{projectDetail.negotiation_flats || 0}</p>
              <p className="text-[10px] text-amber-600">Negotiation</p>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm mb-6">
          <div className="p-3 sm:p-4 flex flex-wrap items-center gap-2 sm:gap-3">
            <div className="relative w-full sm:flex-1 sm:min-w-[180px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search flat number or tower..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10 bg-slate-50 border-0" data-testid="flat-search" />
            </div>
            <div className="flex flex-wrap gap-2 w-full sm:w-auto">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[calc(50%-4px)] sm:w-[130px] bg-slate-50 border-0" data-testid="filter-flat-status"><SelectValue /></SelectTrigger>
                <SelectContent>{FLAT_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={filterBhk} onValueChange={setFilterBhk}>
                <SelectTrigger className="w-[calc(50%-4px)] sm:w-[130px] bg-slate-50 border-0" data-testid="filter-flat-bhk"><SelectValue /></SelectTrigger>
                <SelectContent>{BHK_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
              {uniqueTowers.length > 0 && (
                <Select value={filterTower} onValueChange={setFilterTower}>
                  <SelectTrigger className="w-[calc(50%-4px)] sm:w-[140px] bg-slate-50 border-0"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Towers</SelectItem>
                    {uniqueTowers.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              {uniqueFloors.length > 0 && (
                <Select value={filterFloor} onValueChange={setFilterFloor}>
                  <SelectTrigger className="w-[calc(50%-4px)] sm:w-[120px] bg-slate-50 border-0"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Floors</SelectItem>
                    {uniqueFloors.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="flex items-center gap-2 w-full sm:w-auto justify-between sm:justify-start">
              {(filterStatus !== 'all' || filterBhk !== 'all' || filterTower !== 'all' || filterFloor !== 'all' || searchTerm) && (
                <Button variant="ghost" size="sm" onClick={() => { setFilterStatus('all'); setFilterBhk('all'); setFilterTower('all'); setFilterFloor('all'); setSearchTerm(''); }}>
                  <X className="w-4 h-4 mr-1" /> Clear
                </Button>
              )}
              <span className="text-sm text-slate-500 ml-auto">{filteredFlats.length} flats</span>
            </div>
          </div>
        </div>

        {/* Mobile Flat Cards */}
        <div className="lg:hidden space-y-2 mb-6">
          {filteredFlats.length === 0 ? (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="font-medium text-slate-500">No flats found</p>
              <p className="text-sm text-slate-400">Adjust filters or add new flats</p>
            </div>
          ) : (
            filteredFlats.map(flat => (
              <div key={flat.id} className="bg-white rounded-xl shadow-sm p-3 cursor-pointer active:bg-slate-50" onClick={() => { setSelectedFlat(flat); setIsFlatDetailOpen(true); }} data-testid={`flat-card-${flat.id}`}>
                <div className="flex items-start justify-between mb-1.5">
                  <div>
                    <span className="font-semibold text-sm text-slate-900">{flat.flat_number || '—'}</span>
                    {flat.tower_block && <span className="text-xs text-slate-500 ml-1.5">{flat.tower_block}</span>}
                    {flat.floor && <span className="text-xs text-slate-400 ml-1"> · Floor {flat.floor}</span>}
                  </div>
                  {getStatusBadge(flat.status)}
                </div>
                <div className="flex items-center gap-3 text-xs text-slate-600 flex-wrap">
                  {flat.bhk_type && <span className="font-medium">{flat.bhk_type}</span>}
                  {flat.size_sqft ? <span>{Number(flat.size_sqft).toLocaleString('en-IN')} sq.ft</span> : null}
                  {flat.furnishing && <span className="capitalize">{flat.furnishing}</span>}
                  {flat.facing && <span>{flat.facing}</span>}
                </div>
                {flat.price ? <p className="text-xs font-medium text-slate-800 mt-1">{formatPrice(flat.price)}</p> : <p className="text-[11px] text-slate-400 mt-1">Price not provided</p>}
                {flat.owner_name && <p className="text-[11px] text-slate-400 mt-0.5 truncate">{flat.owner_name}</p>}
              </div>
            ))
          )}
        </div>

        {/* Desktop Flats Table */}
        <div className="hidden lg:block bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="flats-table">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Flat</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Tower/Block</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Floor</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">BHK</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Size</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Price</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Furnishing</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Lead</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Agent</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredFlats.length === 0 ? (
                  <tr><td colSpan={11} className="px-4 py-12 text-center text-slate-500">
                    <Building2 className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p className="font-medium">No flats found</p>
                    <p className="text-sm">Adjust filters or add new flats</p>
                  </td></tr>
                ) : (
                  filteredFlats.map(flat => (
                    <tr key={flat.id} className="hover:bg-slate-50 transition-colors cursor-pointer" onClick={() => { setSelectedFlat(flat); setIsFlatDetailOpen(true); }} data-testid={`flat-row-${flat.id}`}>
                      <td className="px-4 py-3 font-medium text-slate-900">{flat.flat_number}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{flat.tower_block || '-'}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{flat.floor || '-'}</td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <Select value={flat.bhk_type} onValueChange={(v) => handleBhkUpdate(flat.id, v)}>
                          <SelectTrigger className="w-[100px] h-7 text-xs border-0 bg-transparent p-0"><span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">{flat.bhk_type}</span></SelectTrigger>
                          <SelectContent>{BHK_TYPES.filter(t => t.value !== 'all').map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-900">{flat.size_sqft ? `${flat.size_sqft} sq.ft` : '-'}</td>
                      <td className="px-4 py-3 text-sm text-slate-900">{formatPrice(flat.price)}</td>
                      <td className="px-4 py-3 text-sm text-slate-500 capitalize">{flat.furnishing || '-'}</td>
                      <td className="px-4 py-3 text-sm">
                        {(flat.linked_leads || []).length > 0 ? (
                          <span className="flex items-center gap-1 text-xs text-indigo-600"><User className="w-3 h-3" />{(flat.linked_leads || []).length} lead{(flat.linked_leads || []).length > 1 ? 's' : ''}</span>
                        ) : flat.linked_lead_name ? (
                          <span className="flex items-center gap-1 text-xs text-indigo-600"><User className="w-3 h-3" />{flat.linked_lead_name}</span>
                        ) : <span className="text-xs text-slate-400">-</span>}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {flat.assigned_agent_name ? (
                          <span className="flex items-center gap-1 text-xs text-teal-600"><Phone className="w-3 h-3" />{flat.assigned_agent_name}</span>
                        ) : <span className="text-xs text-slate-400">-</span>}
                      </td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <Select value={flat.status} onValueChange={(v) => handleStatusUpdate(flat.id, v)}>
                          <SelectTrigger className="w-[120px] h-7 text-xs border-0 bg-transparent p-0">{getStatusBadge(flat.status)}</SelectTrigger>
                          <SelectContent>{FLAT_STATUSES.filter(s => s.value !== 'all').map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={(e) => { e.stopPropagation(); setSelectedFlat(flat); setIsFlatDetailOpen(true); }}>
                            <Eye className="w-4 h-4 text-slate-400" />
                          </Button>
                          {isAdmin && (
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={(e) => { e.stopPropagation(); handleDeleteFlat(flat.id); }} data-testid={`delete-flat-${flat.id}`}>
                              <Trash2 className="w-4 h-4 text-red-400 hover:text-red-600" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Flat Detail Sheet */}
        <Sheet open={isFlatDetailOpen} onOpenChange={setIsFlatDetailOpen}>
          <SheetContent className="w-full sm:max-w-md overflow-y-auto">
            {selectedFlat && (
              <>
                <SheetHeader className="mb-6">
                  <SheetTitle className="text-xl">Flat {selectedFlat.flat_number}</SheetTitle>
                  <div className="flex items-center gap-2 mt-1">
                    {getStatusBadge(selectedFlat.status)}
                    <span className="text-sm text-slate-500">{selectedFlat.project_name}</span>
                    {selectedFlat.source === '99acres' && <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-600">99acres</span>}
                  </div>
                </SheetHeader>
                <div className="space-y-4">
                  <Card className="p-4">
                    <h3 className="font-semibold text-slate-900 mb-3">Flat Details <span className="text-xs text-slate-400 font-normal">(click to edit)</span></h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div><span className="text-slate-500">Project:</span><p className="font-medium">{selectedFlat.project_name}</p></div>
                      <div><span className="text-slate-500">Tower/Block:</span>
                        <Input className="mt-1 h-7 text-sm" defaultValue={selectedFlat.tower_block || ''} placeholder="e.g., Tower A" onBlur={(e) => { const v = e.target.value.trim(); if (v !== (selectedFlat.tower_block || '')) handleFlatPatch(selectedFlat.id, 'tower_block', v || null); }} /></div>
                      <div><span className="text-slate-500">Flat No:</span><p className="font-medium">{selectedFlat.flat_number}</p></div>
                      <div><span className="text-slate-500">Floor:</span>
                        <Input className="mt-1 h-7 text-sm" defaultValue={selectedFlat.floor || ''} placeholder="e.g., 3rd Floor" onBlur={(e) => { const v = e.target.value.trim(); if (v !== (selectedFlat.floor || '')) handleFlatPatch(selectedFlat.id, 'floor', v || null); }} /></div>
                      <div><span className="text-slate-500">BHK Type:</span><p className="font-medium">{selectedFlat.bhk_type}</p></div>
                      <div><span className="text-slate-500">Size (sq.ft):</span>
                        <Input className="mt-1 h-7 text-sm" defaultValue={selectedFlat.size_sqft || ''} placeholder="e.g., 1200" type="number" onBlur={(e) => { const v = e.target.value; if (v !== String(selectedFlat.size_sqft || '')) handleFlatPatch(selectedFlat.id, 'size_sqft', v || null); }} /></div>
                      <div><span className="text-slate-500">Price (₹):</span>
                        <Input className="mt-1 h-7 text-sm" defaultValue={selectedFlat.price || ''} placeholder="e.g., 9600000" type="number" onBlur={(e) => { const v = e.target.value; if (v !== String(selectedFlat.price || '')) handleFlatPatch(selectedFlat.id, 'price', v || null); }} /></div>
                      <div><span className="text-slate-500">Facing:</span>
                        <Input className="mt-1 h-7 text-sm" defaultValue={selectedFlat.facing || ''} placeholder="e.g., North" onBlur={(e) => { const v = e.target.value.trim(); if (v !== (selectedFlat.facing || '')) handleFlatPatch(selectedFlat.id, 'facing', v || null); }} /></div>
                      <div><span className="text-slate-500">Parking:</span>
                        <Input className="mt-1 h-7 text-sm" defaultValue={selectedFlat.parking || ''} placeholder="e.g., 1, 2, Yes" onBlur={(e) => { const v = e.target.value.trim(); if (v !== (selectedFlat.parking || '')) handleFlatPatch(selectedFlat.id, 'parking', v || null); }} /></div>
                      <div><span className="text-slate-500">Furnishing:</span>
                        <Select value={selectedFlat.furnishing || 'unfurnished'} onValueChange={(v) => handleFlatPatch(selectedFlat.id, 'furnishing', v)}>
                          <SelectTrigger className="mt-1 h-7 text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="furnished">Furnished</SelectItem><SelectItem value="semi-furnished">Semi-Furnished</SelectItem><SelectItem value="unfurnished">Unfurnished</SelectItem></SelectContent>
                        </Select></div>
                      <div><span className="text-slate-500">Status:</span><p className="font-medium capitalize">{selectedFlat.status}</p></div>
                    </div>
                    <div className="mt-3 pt-3 border-t"><span className="text-slate-500 text-sm">Notes:</span>
                      <Textarea className="mt-1 text-sm" defaultValue={selectedFlat.notes || ''} placeholder="Add notes..." rows={2} onBlur={(e) => { const v = e.target.value.trim(); if (v !== (selectedFlat.notes || '')) handleFlatPatch(selectedFlat.id, 'notes', v || null); }} /></div>
                  </Card>

                  {/* Lead & Agent Mapping */}
                  <Card className="p-4">
                    <h3 className="font-semibold text-slate-900 mb-3">Linked Leads <span className="text-xs text-slate-400 font-normal">({(selectedFlat.linked_leads || []).length})</span></h3>
                    {(selectedFlat.linked_leads || []).length > 0 && (
                      <div className="space-y-2 mb-3">
                        {(selectedFlat.linked_leads || []).map(lead => (
                          <div key={lead.id} className="flex items-center justify-between bg-slate-50 rounded-lg px-3 py-2">
                            <div>
                              <p className="text-sm font-medium text-slate-800">{lead.name}</p>
                              {lead.phone && <a href={`tel:${lead.phone}`} className="text-xs text-blue-600">{lead.phone}</a>}
                            </div>
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400 hover:text-red-600" onClick={() => handleRemoveLeadFromFlat(selectedFlat.id, lead.id)}>
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    <Select onValueChange={(v) => handleAssignLead(selectedFlat.id, v)}>
                      <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="+ Add a lead..." /></SelectTrigger>
                      <SelectContent>
                        {leads.filter(l => !(selectedFlat.linked_leads || []).some(ll => ll.id === l.id)).slice(0, 50).map(l => (
                          <SelectItem key={l.id} value={l.id}>{l.name} - {l.phone}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Card>
                  <Card className="p-4">
                    <h3 className="font-semibold text-slate-900 mb-3">Assigned Agent</h3>
                    <div>
                      {selectedFlat.assigned_agent_name ? (
                        <div className="flex items-center justify-between">
                          <p className="font-medium text-teal-600 flex items-center gap-1"><Phone className="w-4 h-4" />{selectedFlat.assigned_agent_name}</p>
                          <Select onValueChange={(v) => handleAssignAgent(selectedFlat.id, v)}>
                            <SelectTrigger className="w-[120px] h-7 text-xs"><SelectValue placeholder="Change" /></SelectTrigger>
                            <SelectContent>
                              {agents.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      ) : (
                        <Select onValueChange={(v) => handleAssignAgent(selectedFlat.id, v)}>
                          <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="Assign an agent..." /></SelectTrigger>
                          <SelectContent>
                            {agents.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </Card>

                  {/* BHK Type Update */}
                  <Card className="p-4">
                    <h3 className="font-semibold text-slate-900 mb-3">Update BHK Type</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {['1 BHK', '2 BHK', '3 BHK', '4 BHK', '5 BHK', 'Penthouse', 'Studio'].map(t => (
                        <Button key={t} size="sm" variant={selectedFlat.bhk_type === t ? 'default' : 'outline'} className="capitalize" onClick={() => handleBhkUpdate(selectedFlat.id, t)}>
                          {t}
                        </Button>
                      ))}
                    </div>
                  </Card>

                  {/* Status Update */}
                  <Card className="p-4">
                    <h3 className="font-semibold text-slate-900 mb-3">Update Status</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {['available', 'booked', 'sold', 'negotiation'].map(s => (
                        <Button key={s} size="sm" variant={selectedFlat.status === s ? 'default' : 'outline'} className="capitalize" onClick={() => handleStatusUpdate(selectedFlat.id, s)}>
                          {s}
                        </Button>
                      ))}
                    </div>
                  </Card>
                  {isAdmin && (
                    <Button variant="outline" className="w-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => handleDeleteFlat(selectedFlat.id)} data-testid="delete-flat-detail">
                      <Trash2 className="w-4 h-4 mr-2" /> Delete Flat
                    </Button>
                  )}
                </div>
              </>
            )}
          </SheetContent>
        </Sheet>

        {/* Add Flat Modal */}
        <Dialog open={isFlatModalOpen} onOpenChange={setIsFlatModalOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Add Flat to {selectedProject.name}</DialogTitle></DialogHeader>
            <form onSubmit={handleCreateFlat} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Flat Number *</Label><Input value={flatForm.flat_number} onChange={(e) => setFlatForm({ ...flatForm, flat_number: e.target.value })} required data-testid="flat-number-input" /></div>
                <div><Label>Tower/Block</Label><Input value={flatForm.tower_block} onChange={(e) => setFlatForm({ ...flatForm, tower_block: e.target.value })} placeholder="e.g., Tower A" /></div>
                <div><Label>Floor</Label><Input value={flatForm.floor} onChange={(e) => setFlatForm({ ...flatForm, floor: e.target.value })} placeholder="e.g., 3rd Floor" /></div>
                <div><Label>BHK Type</Label>
                  <Select value={flatForm.bhk_type} onValueChange={(v) => setFlatForm({ ...flatForm, bhk_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{BHK_TYPES.filter(t => t.value !== 'all').map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Size (sq.ft)</Label><Input type="number" value={flatForm.size_sqft} onChange={(e) => setFlatForm({ ...flatForm, size_sqft: e.target.value })} /></div>
                <div><Label>Price</Label><Input type="number" value={flatForm.price} onChange={(e) => setFlatForm({ ...flatForm, price: e.target.value })} /></div>
                <div><Label>Facing</Label><Input value={flatForm.facing} onChange={(e) => setFlatForm({ ...flatForm, facing: e.target.value })} placeholder="e.g., North" /></div>
                <div><Label>Parking</Label><Input value={flatForm.parking} onChange={(e) => setFlatForm({ ...flatForm, parking: e.target.value })} placeholder="e.g., 1, 2, Yes, No" /></div>
                <div><Label>Furnishing</Label>
                  <Select value={flatForm.furnishing} onValueChange={(v) => setFlatForm({ ...flatForm, furnishing: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{FURNISHING_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Status</Label>
                  <Select value={flatForm.status} onValueChange={(v) => setFlatForm({ ...flatForm, status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{FLAT_STATUSES.filter(s => s.value !== 'all').map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Assign Agent</Label>
                  <Select value={flatForm.assigned_agent} onValueChange={(v) => setFlatForm({ ...flatForm, assigned_agent: v })}>
                    <SelectTrigger><SelectValue placeholder="Select agent" /></SelectTrigger>
                    <SelectContent>
                      {agents.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Link Lead</Label>
                  <Select value={flatForm.linked_lead_id} onValueChange={(v) => setFlatForm({ ...flatForm, linked_lead_id: v })}>
                    <SelectTrigger><SelectValue placeholder="Select lead" /></SelectTrigger>
                    <SelectContent>
                      {leads.slice(0, 50).map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label>Notes</Label><Textarea value={flatForm.notes} onChange={(e) => setFlatForm({ ...flatForm, notes: e.target.value })} rows={2} /></div>
              <Button type="submit" className="w-full bg-slate-900 hover:bg-slate-800" data-testid="submit-flat">Add Flat</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Properties;
